'use client';

import React from 'react';
import { Container, Title, Text, Button, Stack, Center } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import Link from 'next/link';
import useSafeTranslation from '@/hooks/useSafeTranslation';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  const { tSafe: t } = useSafeTranslation();

  React.useEffect(() => {
    // Log the error to an error reporting service in production
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error);
    } else {
      // eslint-disable-next-line no-console
      console.error('App error:', error);
    }
  }, [error]);

  return (
    <Container size="sm" py="xl">
      <Center mih="60vh">
        <Stack align="center" gap="lg">
          <IconAlertTriangle size={48} color="red" />
          <Title order={1}>{t('errorTitle') ?? 'Something went wrong'}</Title>
          <Text c="dimmed" ta="center">
            {t('errorDescription') ??
              'An unexpected error occurred. Please try again or contact support.'}
          </Text>
          {process.env.NODE_ENV !== 'production' && error.message && (
            <Text size="sm" c="red" style={{ wordBreak: 'break-word' }}>
              {error.message}
            </Text>
          )}
          <Stack gap="sm" mt="lg">
            <Button onClick={() => reset()}>{t('errorRetry') ?? 'Try again'}</Button>
            <Link href="/">
              <Button variant="light">{t('errorHome') ?? 'Go home'}</Button>
            </Link>
          </Stack>
        </Stack>
      </Center>
    </Container>
  );
}
